import { c as _c } from "react/compiler-runtime";
import { useTheme } from 'next-themes';

// Foregrounds mirror `expression-editor-theme` in
// packages/ui/src/components/monaco/themes.ts so this static preview matches the
// editable Monaco editor. `monaco.editor.colorize` only colors when the
// expression tokenizer is registered (i.e. an editor is mounted) and its theme is
// active — neither holds for a collapsed condition row — so we highlight directly.
const PALETTE = {
  light: {
    keyword: '#c259bb',
    string: '#3d9d7d',
    number: '#24585b',
    operator: '#d73a49',
    identifier: '#24292e'
  },
  dark: {
    keyword: '#d2a8ff',
    string: '#a5d6ff',
    number: '#79c0ff',
    operator: '#ff7b72',
    identifier: '#e6edf3'
  }
} as const;
type TokenKind = keyof (typeof PALETTE)['light'] | 'plain';

// Ordered so earlier patterns win; the trailing catch-all guarantees a match.
const MATCHERS: {
  re: RegExp;
  kind: TokenKind;
}[] = [{
  re: /^\s+/,
  kind: 'plain'
}, {
  re: /^("[^"]*"|'[^']*')/,
  kind: 'string'
}, {
  re: /^\d+\.?\d*/,
  kind: 'number'
}, {
  re: /^(outputs|inputs|true|false|null)\b/,
  kind: 'keyword'
}, {
  re: /^[+\-*/=<>!&|?%]+/,
  kind: 'operator'
}, {
  re: /^[.(),;[\]]/,
  kind: 'keyword'
}, {
  re: /^[A-Za-z_]\w*/,
  kind: 'identifier'
}, {
  re: /^[\s\S]/,
  kind: 'plain'
}];
type Token = {
  text: string;
  kind: TokenKind;
  start: number;
};
const tokenize = (expression: string): Token[] => {
  const tokens: Token[] = [];
  let rest = expression;
  let start = 0;
  while (rest.length > 0) {
    const matcher = MATCHERS.find(m => m.re.test(rest));
    const matched = matcher ? (rest.match(matcher.re) as RegExpMatchArray)[0] : rest[0];
    tokens.push({
      text: matched,
      kind: matcher?.kind ?? 'plain',
      start
    });
    rest = rest.slice(matched.length);
    start += matched.length;
  }
  return tokens;
};
type HighlightedExpressionProps = {
  value: string;
};
const HighlightedExpression = t0 => {
  const $ = _c(7);
  const {
    value
  } = t0;
  const {
    resolvedTheme
  } = useTheme();
  const palette = resolvedTheme === "dark" ? PALETTE.dark : PALETTE.light;
  let t1;
  if ($[0] !== palette || $[1] !== value) {
    let t2;
    if ($[3] !== palette) {
      t2 = token => {
        if (token.kind === "plain") {
          return <span key={token.start}>{token.text}</span>;
        }
        return <span key={token.start} style={{
          color: palette[token.kind]
        }}>{token.text}</span>;
      };
      $[3] = palette;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = tokenize(value).map(t2);
    $[0] = palette;
    $[1] = value;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <span>{t1}</span>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
export default HighlightedExpression;